import { useState } from "react";
import { toast } from "sonner";
import { Loader2, TriangleAlert } from "lucide-react";
import { Button } from "@/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/select";
import { captureTelemetryEvent } from "@/app/telemetry/posthog";
import {
  useUpdateWorkspaceRetentionMutation,
  useWorkspaceQuery,
  type AudioRetentionDays,
  type TranscriptRetentionDays,
  type WorkspaceRetentionUpdate,
} from "@/data/workspaces";
import {
  FormCard,
  FormRow,
  InlineMessage,
  SettingsHeader,
} from "./settings-ui";

const AUDIO_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "0", label: "Delete after processing" },
  { value: "1", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days (default)" },
];

const TRANSCRIPT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "forever", label: "Forever (default)" },
  { value: "365", label: "1 year" },
  { value: "90", label: "90 days" },
  { value: "30", label: "30 days" },
];

function transcriptDaysLabel(days: TranscriptRetentionDays): string {
  return days === 365 ? "1 year" : `${days} days`;
}

export function PrivacySection({
  workspaceSlug,
  canManage,
}: {
  workspaceSlug: string | null;
  canManage: boolean;
}) {
  const workspaceQuery = useWorkspaceQuery(workspaceSlug);
  const updateRetention = useUpdateWorkspaceRetentionMutation(workspaceSlug);
  const workspace = workspaceQuery.data?.workspace ?? null;
  // A pending "shorten retention" choice awaiting explicit confirmation —
  // both dials delete existing data retroactively.
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);

  const disabled = !canManage || !workspace || updateRetention.isPending;

  async function save(update: WorkspaceRetentionUpdate) {
    if (!workspace) return;
    const previous = {
      audio_retention_days: workspace.audio_retention_days,
      transcript_retention_days: workspace.transcript_retention_days,
      retain_enrollment_audio: workspace.retain_enrollment_audio,
    };
    try {
      await updateRetention.mutateAsync(update);
      captureTelemetryEvent("retention_policy_updated", {
        workspace_id: workspace.id,
        previous,
        update,
      });
      toast.success("Retention policy updated");
    } catch {
      toast.error("Failed to update retention policy");
    }
  }

  function onAudioChange(value: string) {
    const next = Number(value) as AudioRetentionDays;
    if (!workspace) return;
    const current = workspace.audio_retention_days ?? 30;
    if (next < current) {
      // Shortening deletes existing audio — get explicit confirmation.
      setConfirm({ kind: "audio", days: next });
      return;
    }
    void save({ audio_retention_days: next });
  }

  function onTranscriptChange(value: string) {
    const next =
      value === "forever" ? null : (Number(value) as TranscriptRetentionDays);
    if (!workspace) return;
    const currentRank = workspace.transcript_retention_days ?? Infinity;
    const nextRank = next ?? Infinity;
    if (next !== null && nextRank < currentRank) {
      // Shortening deletes existing conversations — get explicit confirmation.
      setConfirm({ kind: "transcript", days: next });
      return;
    }
    void save({ transcript_retention_days: next });
  }

  const audioValue = String(workspace?.audio_retention_days ?? 30);
  const transcriptValue =
    workspace?.transcript_retention_days == null
      ? "forever"
      : String(workspace.transcript_retention_days);

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="Privacy"
        description="Control how long Parable keeps your workspace's data. Policies apply to existing data and are enforced within the hour."
      />

      <div className="space-y-6">
        {!canManage ? (
          <InlineMessage variant="info">
            Only workspace admins can change the retention policy.
          </InlineMessage>
        ) : null}

        <FormCard>
          <FormRow
            label="Conversation audio"
            hint="How long raw audio from your devices is kept. Transcripts are not affected. Shorter settings reduce what could ever leak, but past conversations can't be re-processed when speaker recognition improves."
          >
            <Select
              value={audioValue}
              disabled={disabled}
              onValueChange={onAudioChange}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIO_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormRow>

          <FormRow
            label="Transcripts"
            hint="How long conversations — transcript, summary, and speaker data — are kept, measured from when the conversation happened."
          >
            <Select
              value={transcriptValue}
              disabled={disabled}
              onValueChange={onTranscriptChange}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSCRIPT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormRow>

          <FormRow
            label="Voice test recordings"
            hint="The short read-aloud recordings used to set up speaker recognition. Turn retention off and each recording is deleted once setup completes — recognition keeps working from the voiceprint, a numeric fingerprint that isn't audio and can't be played back."
          >
            <label className="inline-flex h-8 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-[13px] text-muted-foreground">
              <input
                type="checkbox"
                checked={workspace?.retain_enrollment_audio ?? true}
                disabled={disabled}
                onChange={(event) =>
                  void save({
                    retain_enrollment_audio: event.currentTarget.checked,
                  })
                }
              />
              Keep recordings
            </label>
          </FormRow>
        </FormCard>
      </div>

      {confirm != null && workspace ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="retention-confirm-title"
            className="w-full max-w-md rounded-lg border border-border bg-card shadow-2xl"
          >
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <TriangleAlert className="h-4 w-4" />
              </span>
              <h3
                id="retention-confirm-title"
                className="text-[14px] font-semibold text-foreground"
              >
                {confirmCopy(confirm).title}
              </h3>
            </div>
            <div className="space-y-2 px-4 py-4 text-[13px] text-muted-foreground">
              <p>{confirmCopy(confirm).body}</p>
              <p>This can't be undone.</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={updateRetention.isPending}
                onClick={() => setConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={updateRetention.isPending}
                onClick={() => {
                  const update: WorkspaceRetentionUpdate =
                    confirm.kind === "audio"
                      ? { audio_retention_days: confirm.days }
                      : { transcript_retention_days: confirm.days };
                  void save(update).then(() => setConfirm(null));
                }}
              >
                {updateRetention.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : null}
                {confirmCopy(confirm).action}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

type PendingConfirm =
  | { kind: "audio"; days: AudioRetentionDays }
  | { kind: "transcript"; days: TranscriptRetentionDays };

function confirmCopy(confirm: PendingConfirm): {
  title: string;
  body: string;
  action: string;
} {
  if (confirm.kind === "audio") {
    if (confirm.days === 0) {
      return {
        title: "Delete all stored conversation audio?",
        body: "Audio for every already-processed conversation is permanently deleted within the hour, and future recordings are deleted the moment they finish processing. Transcripts and summaries are not affected, but these conversations can never be re-processed with improved speaker recognition.",
        action: "Delete stored audio",
      };
    }
    return {
      title: `Delete conversation audio older than ${confirm.days} days?`,
      body: `This applies to audio you already have. Recordings older than ${confirm.days} days are permanently deleted within the hour, and new recordings are deleted as they pass that age. Transcripts and summaries are not affected, but those conversations can never be re-processed with improved speaker recognition.`,
      action: "Delete older audio",
    };
  }
  const label = transcriptDaysLabel(confirm.days);
  return {
    title: `Delete conversations older than ${label}?`,
    body: `This applies to conversations you already have. Anything older than ${label} — transcript, summary, and audio — is permanently deleted within the hour, and new conversations are deleted as they pass that age.`,
    action: "Delete older conversations",
  };
}
